import { useEffect, useState } from "react";
import api from "../../lib/api";
import { REPORT_STATUS } from "../../lib/missionReports";
import { STATUS_META, describeWarning } from "../../lib/missions";
import { DEFAULT_LEVEL_LABELS } from "../../lib/skills";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "../ui/command";
import { ResponsivePopover as Popover, ResponsivePopoverContent as PopoverContent, ResponsivePopoverTrigger as PopoverTrigger } from "../ui/responsive-popover";
import { AlertTriangle, ChevronDown, IdCard, User } from "lucide-react";

// Small pieces shared by the missions list, form and page.

export function MissionStatusBadge({ status, className = "" }) {
  const meta = STATUS_META[status] || { label: status, className: "bg-slate-100 text-slate-700 border-slate-200" };
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium whitespace-nowrap ${meta.className} ${className}`} data-testid={`mission-status-${status}`}>
      {meta.label}
    </span>
  );
}

// The validation state of an ended mission's report (Ordre 15): shown beside the mission's status, which stays « Terminée ».
export function ReportStateBadge({ status, className = "" }) {
  const meta = REPORT_STATUS[status];
  if (!meta || status === "draft") return null;
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium whitespace-nowrap ${meta.tone} ${className}`} data-testid={`mission-report-${status}`}>
      {meta.short}
    </span>
  );
}

export const levelLabel = (code) => DEFAULT_LEVEL_LABELS[code] || code;

// The warnings the server returned (409 `warnings`): never blocking, the person reads them and confirms.
export function WarningsNotice({ warnings, confirmed, onConfirmedChange, testid = "mission-warnings" }) {
  if (!warnings?.length) return null;
  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 space-y-2" role="alert" data-testid={testid}>
      <div className="flex items-center gap-2 text-sm font-medium text-amber-900"><AlertTriangle size={15} /> À vérifier avant de confirmer</div>
      <ul className="space-y-1 text-sm text-amber-900 list-disc pl-5">
        {warnings.map((w, i) => <li key={i}>{describeWarning(w, levelLabel)}</li>)}
      </ul>
      <label className="flex items-start gap-2 text-sm text-amber-900 cursor-pointer">
        <Checkbox checked={confirmed} onCheckedChange={(v) => onConfirmedChange(!!v)} className="mt-0.5" data-testid={`${testid}-confirm`} />
        <span>J'ai vérifié : continuer malgré ces avertissements (ils resteront dans l'historique).</span>
      </label>
    </div>
  );
}

// Picks a CRM account or, with the directory access, a collaborator fiche without account (GET /missions/candidates).
// `exclude` = "kind:id" keys already in the team.
export function MemberPicker({ value, onChange, exclude = [], testid = "mission-member-picker", placeholder = "Choisir une personne…" }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [result, setResult] = useState({ accounts: [], fiches: [] });
  const [state, setState] = useState("idle"); // idle | loading | error

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setState("loading");
    const handle = setTimeout(async () => {
      try {
        const { data } = await api.get("/missions/candidates", { params: { search: query.trim(), limit: 20 } });
        if (!cancelled) { setResult(data); setState("idle"); }
      } catch (e) {
        if (!cancelled) setState("error");
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(handle); };
  }, [query, open]);

  const skip = new Set(exclude);
  const accounts = result.accounts.filter((a) => !skip.has(`user:${a.id}`));
  const fiches = result.fiches.filter((f) => !skip.has(`collaborator:${f.id}`));
  const pick = (member) => { onChange(member); setOpen(false); };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" className="w-full justify-between font-normal" data-testid={testid}>
          <span className="truncate">{value ? value.name : <span className="text-slate-500">{placeholder}</span>}</span>
          <ChevronDown size={14} className="ml-2 shrink-0 text-slate-400" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[min(22rem,calc(100vw-2rem))]" align="start" title="Choisir une personne">
        <Command shouldFilter={false}>
          <CommandInput value={query} onValueChange={setQuery} placeholder="Nom…" data-testid={`${testid}-search`} />
          <CommandList>
            {state === "loading" && <div className="py-6 text-center text-xs text-slate-400">Recherche…</div>}
            {state === "error" && <div className="py-6 text-center text-xs text-red-700">Impossible de charger la liste. Réessayez.</div>}
            {state === "idle" && accounts.length === 0 && fiches.length === 0 && <CommandEmpty>Personne ne correspond.</CommandEmpty>}
            {state === "idle" && accounts.length > 0 && (
              <CommandGroup heading="Comptes CRM">
                {accounts.map((a) => (
                  <CommandItem key={a.id} value={`user:${a.id}`} onSelect={() => pick({ kind: "user", id: a.id, name: a.name })} data-testid={`${testid}-user-${a.id}`}>
                    <User size={14} className="mr-2 text-slate-400" />{a.name}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            {state === "idle" && fiches.length > 0 && (
              <CommandGroup heading="Collaborateurs sans compte">
                {fiches.map((f) => (
                  <CommandItem key={f.id} value={`collaborator:${f.id}`} onSelect={() => pick({ kind: "collaborator", id: f.id, name: f.name })} data-testid={`${testid}-fiche-${f.id}`}>
                    <IdCard size={14} className="mr-2 text-slate-400" />
                    <span className="truncate">{f.name}</span>
                    {f.job_title && <span className="ml-2 truncate text-xs text-slate-400">{f.job_title}</span>}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export function MemberKindHint({ kind }) {
  return kind === "collaborator"
    ? <span className="text-xs text-slate-500">Sans compte CRM : affecté, mais sans accès</span>
    : null;
}
